import React from 'react';
import { Modal } from 'is24-corecss';
import { BenefitModalProps } from '@/@types/benefitModalProps';
import Msg from '@/i18n/components/FormattedMessage';
import { useIntl } from '@/i18n/hooks/useIntl';
import RechtsberatungJpg from '@/components/content/benefits/modals/rechtsberatung/images/digital_address.png';
import RechtsberatungWebp from '@/components/content/benefits/modals/rechtsberatung/images/digital_address.webp';
import RechtsberatungJpgMobile from '@/components/content/benefits/modals/rechtsberatung/images/digital_address_mobile.png';
import RechtsberatungWebpMobile from '@/components/content/benefits/modals/rechtsberatung/images/digital_address_mobile.webp';
import { Benefit } from '@/@types/benefits';
import { APP_CONFIG } from '@/config';
import { AddressChangeStatus } from '@/@types/addressChange';
import { useGetAddressChangeStatus } from '@/apiHooks/addressChange.hook';
import { GenericLoader } from '@/components/common/display/loader/GenericLoader';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { ADDRESS_CHANGE_STANDALONE_PAYMENT_URL, campaigns } from '@/components/common/campaigns';
import { FINO_PATH } from '@/domain/routes';
import { TrackingEvent } from '@/@types/trackingEvent';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import { appendQueryParams } from '@/utils/generateQueryParams.util';
import ModalV2 from '@/components/common/display/modal/ModalV2';
import './AddressChange.css';

// URL Constants
const FINO_PAGE_URL = `${APP_CONFIG.baseUrl}${APP_CONFIG.publicUrl}${FINO_PATH}`;

// Tracking Keys
const TRANSLATION_PREFIX = 'benefits.addressChange.modal';
const TRANSLATION_KEY = `${TRANSLATION_PREFIX}.overview`;

interface ModalState {
  showBulletPoints: boolean;
  translationKey: string;
  callToActionUrl: string;
  report?: TrackingEvent;
}

const MODAL_STATE_CONFIG: Record<AddressChangeStatus, ModalState> = {
  [AddressChangeStatus.START_PREMIUM]: {
    showBulletPoints: true,
    translationKey: `${TRANSLATION_KEY}.${AddressChangeStatus.START_PREMIUM}`,
    callToActionUrl: FINO_PAGE_URL,
    report: TrackingEvents.ADDRESS_CHANGE.LINK_CLICKED,
  },
  [AddressChangeStatus.CONTINUE]: {
    showBulletPoints: false,
    translationKey: `${TRANSLATION_KEY}.${AddressChangeStatus.CONTINUE}`,
    callToActionUrl: FINO_PAGE_URL,
    report: TrackingEvents.ADDRESS_CHANGE.LINK_CLICKED,
  },
  [AddressChangeStatus.START_STANDALONE]: {
    showBulletPoints: false,
    translationKey: `${TRANSLATION_KEY}.${AddressChangeStatus.CONTINUE}`,
    callToActionUrl: FINO_PAGE_URL,
  },
  [AddressChangeStatus.NOT_ELIGIBLE_USED_MP]: {
    showBulletPoints: true,
    translationKey: `${TRANSLATION_KEY}.${AddressChangeStatus.NOT_ELIGIBLE_USED_MP}`,
    callToActionUrl: `${ADDRESS_CHANGE_STANDALONE_PAYMENT_URL}?returnUrl=${encodeURIComponent(FINO_PAGE_URL)}`,
    report: TrackingEvents.ADDRESS_CHANGE.LINK_CLICKED,
  },
  [AddressChangeStatus.NOT_ELIGIBLE]: {
    showBulletPoints: true,
    translationKey: `${TRANSLATION_KEY}.${AddressChangeStatus.NOT_ELIGIBLE}`,
    callToActionUrl: `${ADDRESS_CHANGE_STANDALONE_PAYMENT_URL}?returnUrl=${encodeURIComponent(FINO_PAGE_URL)}`,
  },
};

const styles = {
  overviewTab: { maxWidth: 530 },
};

const OverviewTab = ({ initializing, state }: { initializing: boolean; state: ModalState }) => {
  if (initializing) {
    return (
      <div data-testid='overviewLoader' className='one-whole' style={styles.overviewTab}>
        <GenericLoader className='margin-bottom-l' width='55%' height='30px' />
        <GenericLoader width='100%' height='45px' />
      </div>
    );
  }

  return (
    <div data-testid='overviewTab' style={styles.overviewTab}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg id={`${state.translationKey}.paragraph1`} defaultMessage='Are you moving soon...' />
      </div>
      <div className='padding-bottom-l'>
        <Msg id={`${state.translationKey}.paragraph2`} defaultMessage='We will recognize & notify...' />
      </div>
      {state.showBulletPoints && (
        <ul data-testid='bullets' className='list-check list-spacing-s'>
          <li>
            <Msg id={`${state.translationKey}.bullet1`} defaultMessage='All digital...' />
          </li>
          <li>
            <Msg id={`${state.translationKey}.bullet2`} defaultMessage='Under 5 minutes...' />
          </li>
          <li>
            <Msg id={`${state.translationKey}.bullet3`} defaultMessage='Highest security ...' />
          </li>
        </ul>
      )}
    </div>
  );
};

export const AddressChange = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const { initializing, status } = useGetAddressChangeStatus();
  const state = MODAL_STATE_CONFIG[status || AddressChangeStatus.NOT_ELIGIBLE];
  const { parentPath } = useUrlState();

  const campaignParam =
    campaigns.ADDRESS_CHANGE_BENEFIT_MAPPING[parentPath as keyof typeof campaigns.ADDRESS_CHANGE_BENEFIT_MAPPING] || '';
  const finalUrl = appendQueryParams(state.callToActionUrl, campaignParam);

  const handleCTAClick = () => {
    if (state.report) {
      report(TrackingEvents.ADDRESS_CHANGE.LINK_CLICKED, true);
    }
    window.location.href = finalUrl;
  };

  return (
    <ModalV2
      title={t({ id: `${TRANSLATION_PREFIX}.headline`, defaultMessage: 'Address Change' })}
      isVisible={isVisible}
      image={RechtsberatungJpg}
      imageWebp={RechtsberatungWebp}
      imageMobile={RechtsberatungJpgMobile}
      imageMobileWebp={RechtsberatungWebpMobile}
      imageAlt={t({
        id: `${TRANSLATION_PREFIX}.image`,
        defaultMessage: 'Address Change',
      })}
      imageUseFullWidth={true}
      tabsContent={[<OverviewTab initializing={initializing} state={state} key={TRANSLATION_KEY} />]}
      benefit={Benefit.ADDRESS_CHANGE}
      onClose={onClose}
      {...(!initializing && {
        primaryFooterBtn: (
          <Modal.FooterPrimaryBtn
            label={t({ id: `${state.translationKey}.ctaText`, defaultMessage: 'Discover Mieter Plus' })}
            onClick={handleCTAClick}
          />
        ),
      })}
    />
  );
};
